import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { initCuelume } from './cuelume';
import { MusicProvider } from './music';
import LoadingScreen from './components/LoadingScreen';
import TabBar from './components/TabBar';
import Home from './pages/Home';
import PairGame from './pages/PairGame';
import Match3 from './pages/Match3';
import SortGame from './pages/SortGame';
import HigherGame from './pages/HigherGame';
import EnglishGame from './pages/EnglishGame';
import Leaderboard from './pages/Leaderboard';
import Rules from './pages/Rules';
import IframePage from './pages/IframePage';

const TAB_PAGES = new Set(['home', 'players', 'rules']);

const GAME_PAGES = {
  'pair-game': PairGame,
  'match-3': Match3,
  'sort-game': SortGame,
  'higher-game': HigherGame,
  english: EnglishGame,
};

function pathWithoutSlash() {
  return window.location.pathname.replace(/\/$/, '');
}

function isIframeLocation() {
  return pathWithoutSlash().endsWith('/iframe') || window.location.hash.includes('iframe');
}

function isEnglishLocation() {
  return pathWithoutSlash().endsWith('/english') || window.location.hash.includes('english');
}

function getPageFromLocation() {
  if (isIframeLocation()) return 'iframe';
  if (isEnglishLocation()) return 'english';
  const hash = window.location.hash;
  if (hash.includes('pair-game')) return 'pair-game';
  if (hash.includes('match-3')) return 'match-3';
  if (hash.includes('sort-game')) return 'sort-game';
  if (hash.includes('higher-game')) return 'higher-game';
  if (hash.includes('players')) return 'players';
  if (hash.includes('rules')) return 'rules';
  return 'home';
}

function tabIdForPage(page) {
  if (page === 'players') return 'players';
  if (page === 'rules') return 'rules';
  return 'games';
}

function App() {
  const [page, setPage] = useState(getPageFromLocation);
  // jsdom never fires image load events, so tests skip the loading screen
  const [ready, setReady] = useState(process.env.NODE_ENV === 'test');
  const pageRef = useRef(page);
  pageRef.current = page;

  // Navigation happens through the hash so browser back/forward works.
  // View Transitions stay for entering/leaving games; tab swaps stay instant
  // so only the tab pill slides.
  useEffect(() => {
    const handleHashChange = () => {
      const next = getPageFromLocation();
      const from = pageRef.current;
      const tabSwap = TAB_PAGES.has(from) && TAB_PAGES.has(next);
      if (!tabSwap && document.startViewTransition) {
        document.startViewTransition(() => setPage(next));
      } else {
        setPage(next);
      }
      // Hash routes don't reload the page, so Metrika needs an explicit hit
      if (typeof window.ym === 'function') {
        window.ym(111691257, 'hit', window.location.href);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    initCuelume();
  }, []);

  const handleLoaded = useCallback(() => setReady(true), []);

  const goHome = () => {
    if (pathWithoutSlash().endsWith('/english')) {
      window.location.href = '/';
      return;
    }
    window.location.hash = '/';
  };

  if (page === 'iframe') {
    return <IframePage />;
  }

  // Direct link /english (and /#/english) opens the board immediately,
  // without the branded loading screen used on the main site.
  if (page === 'english') {
    return <EnglishGame onExit={goHome} />;
  }

  if (!ready) {
    return <LoadingScreen onDone={handleLoaded} />;
  }

  const Game = GAME_PAGES[page];
  let content;
  if (Game) {
    content = <Game onExit={goHome} />;
  } else if (page === 'players') {
    content = <Leaderboard />;
  } else if (page === 'rules') {
    content = <Rules />;
  } else {
    content = (
      <Home
        onOpen={(hash) => {
          window.location.hash = hash;
        }}
      />
    );
  }

  return (
    <MusicProvider>
      {content}
      {TAB_PAGES.has(page) && <TabBar active={tabIdForPage(page)} />}
    </MusicProvider>
  );
}

export default App;
